import React, { useState } from 'react';
import { ArrowRight, Wallet, PiggyBank, Building2, Coins, Heart, GraduationCap, Gift, CreditCard, Landmark, DollarSign, ExternalLink, Calculator, Briefcase, Target, Leaf, ShoppingBag, Smartphone, Fuel, AlertCircle, CheckCircle2, XCircle, Receipt, ChevronDown } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { Navigation } from '../components/Navigation';
import { TaxFiling } from '../components/TaxFiling';
import { motion, AnimatePresence } from 'framer-motion';

interface EligibilityProps {
  requirements: string[];
  benefits: string[];
  considerations: string[];
}

interface CollapsibleSectionProps {
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
  defaultOpen?: boolean;
}

const CollapsibleSection: React.FC<CollapsibleSectionProps> = ({ title, icon, children, defaultOpen = false }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className="glass-card p-6 mb-6">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between gap-3 mb-4"
      >
        <div className="flex items-center gap-3">
          <div className="p-3 rounded-xl bg-emerald-500/10">
            {icon}
          </div>
          <h2 className="text-2xl font-semibold">{title}</h2>
        </div>
        <ChevronDown className={`w-6 h-6 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden"
          >
            {children}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const EligibilityGuide: React.FC<EligibilityProps> = ({ requirements, benefits, considerations }) => (
  <div className="mt-4 bg-slate-800/30 p-4 rounded-lg space-y-4">
    <div className="space-y-2">
      <h4 className="text-emerald-500 font-medium flex items-center gap-2">
        <CheckCircle2 className="w-4 h-4" />
        Eligibility Requirements
      </h4>
      <ul className="space-y-1">
        {requirements.map((req, index) => (
          <li key={index} className="text-sm text-slate-300 flex items-start gap-2">
            <ArrowRight className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
            {req}
          </li>
        ))}
      </ul>
    </div>
    
    <div className="space-y-2">
      <h4 className="text-emerald-500 font-medium flex items-center gap-2">
        <Coins className="w-4 h-4" />
        Potential Benefits
      </h4>
      <ul className="space-y-1">
        {benefits.map((benefit, index) => (
          <li key={index} className="text-sm text-slate-300 flex items-start gap-2">
            <ArrowRight className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
            {benefit}
          </li>
        ))}
      </ul>
    </div>

    <div className="space-y-2">
      <h4 className="text-emerald-500 font-medium flex items-center gap-2">
        <AlertCircle className="w-4 h-4" />
        Key Considerations
      </h4>
      <ul className="space-y-1">
        {considerations.map((consideration, index) => (
          <li key={index} className="text-sm text-slate-300 flex items-start gap-2">
            <ArrowRight className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
            {consideration}
          </li>
        ))}
      </ul>
    </div>
  </div>
);

const SavingsBlog = () => {
  return (
    <div className="min-h-screen bg-[var(--background)] p-6">
      <div className="max-w-4xl mx-auto">
        <Navigation />
        
        <header className="text-center mb-12">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <h1 className="text-4xl font-bold mb-4 flex items-center justify-center gap-3">
              <PiggyBank className="w-10 h-10 text-emerald-500" />
              Smart Saving Strategies
            </h1>
            <p className="text-slate-400 text-lg">
              Comprehensive guide to maximize your savings through various strategies
            </p>
          </motion.div>
        </header>

        <div className="space-y-8">
          {/* Quick Links */}
          <div className="grid md:grid-cols-3 gap-4 mb-12">
            <a 
              href="https://refer.intuit.com/drewmaster35"
              target="_blank"
              rel="noopener noreferrer"
              className="glass-card p-6 hover:scale-105 transition-transform"
            >
              <Receipt className="w-8 h-8 text-emerald-500 mb-3" />
              <h3 className="font-semibold mb-2">File Taxes with TurboTax</h3>
              <p className="text-sm text-slate-400">Get your maximum refund guaranteed</p>
            </a>
            <a 
              href="https://upside.app.link/DREW22832"
              target="_blank"
              rel="noopener noreferrer"
              className="glass-card p-6 hover:scale-105 transition-transform"
            >
              <Fuel className="w-8 h-8 text-emerald-500 mb-3" />
              <h3 className="font-semibold mb-2">Save on Gas with Upside</h3>
              <p className="text-sm text-slate-400">Earn cashback on every fill-up</p>
            </a>
            <a 
              href="https://americanexpress.com/en-us/referral/gold-card?ref=ANDREYVhmR&XL=MIMNS"
              target="_blank"
              rel="noopener noreferrer"
              className="glass-card p-6 hover:scale-105 transition-transform"
            >
              <CreditCard className="w-8 h-8 text-emerald-500 mb-3" />
              <h3 className="font-semibold mb-2">Amex Gold Card</h3>
              <p className="text-sm text-slate-400">Earn rewards on dining & groceries</p>
            </a>
          </div>

          <CollapsibleSection 
            title="Tax Filing" 
            icon={<Receipt className="w-6 h-6 text-emerald-500" />}
            defaultOpen={true}
          >
            <TaxFiling />
          </CollapsibleSection>

          <CollapsibleSection 
            title="Retirement Accounts" 
            icon={<Landmark className="w-6 h-6 text-emerald-500" />}
          >
            <div className="space-y-6">
              <div className="glass-card p-6 border border-emerald-500/20">
                <h3 className="text-xl font-semibold text-emerald-500 mb-3">401(k) Plans</h3>
                <ul className="space-y-3">
                  <li className="flex items-start gap-2">
                    <ArrowRight className="w-5 h-5 text-emerald-500 mt-1 flex-shrink-0" />
                    <p className="text-slate-300">Contribute up to $22,500 annually (2024), plus $7,500 catch-up if 50 or older</p>
                  </li>
                  <li className="flex items-start gap-2">
                    <ArrowRight className="w-5 h-5 text-emerald-500 mt-1 flex-shrink-0" />
                    <p className="text-slate-300">Employer matching is essentially free money - always contribute enough to get full match</p>
                  </li>
                  <li className="flex items-start gap-2">
                    <ArrowRight className="w-5 h-5 text-emerald-500 mt-1 flex-shrink-0" />
                    <p className="text-slate-300">Tax-deferred growth and immediate tax benefits through pre-tax contributions</p>
                  </li>
                </ul>
                <EligibilityGuide
                  requirements={[
                    "Must be employed by a company that offers a 401(k) plan",
                    "No income limits for traditional 401(k) contributions",
                    "Must be 21 years or older in most cases",
                    "Usually need to work 1 year before employer match eligibility"
                  ]}
                  benefits={[
                    "Immediate tax deduction on contributions",
                    "Employer matching contributions (typically 3-6% of salary)",
                    "Higher contribution limits than IRAs",
                    "Loan provisions available in many plans"
                  ]}
                  considerations={[
                    "Early withdrawal penalties before age 59½",
                    "Required Minimum Distributions (RMDs) starting at age 73",
                    "Limited investment options compared to IRAs",
                    "Consider your tax bracket now vs. retirement"
                  ]}
                />
              </div>

              <div className="glass-card p-6 border border-emerald-500/20">
                <h3 className="text-xl font-semibold text-emerald-500 mb-3">Roth IRA Benefits</h3>
                <ul className="space-y-3">
                  <li className="flex items-start gap-2">
                    <ArrowRight className="w-5 h-5 text-emerald-500 mt-1 flex-shrink-0" />
                    <p className="text-slate-300">Contribute up to $7,000 annually (2024) with tax-free growth and withdrawals</p>
                  </li>
                  <li className="flex items-start gap-2">
                    <ArrowRight className="w-5 h-5 text-emerald-500 mt-1 flex-shrink-0" />
                    <p className="text-slate-300">Backdoor Roth IRA strategy for high-income earners</p>
                  </li>
                  <li className="flex items-start gap-2">
                    <ArrowRight className="w-5 h-5 text-emerald-500 mt-1 flex-shrink-0" />
                    <p className="text-slate-300">More flexible withdrawal rules compared to traditional retirement accounts</p>
                  </li>
                </ul>
                <EligibilityGuide
                  requirements={[
                    "Must have earned income",
                    "Single filers: Modified AGI < $153,000 (2024) for full contribution",
                    "Married filing jointly: Modified AGI < $242,000 (2024) for full contribution",
                    "No age restrictions for contributions"
                  ]}
                  benefits={[
                    "Tax-free withdrawals in retirement",
                    "No Required Minimum Distributions (RMDs)",
                    "Can withdraw contributions (not earnings) penalty-free",
                    "Ideal for those expecting higher tax rates in retirement"
                  ]}
                  considerations={[
                    "No immediate tax deduction for contributions",
                    "Five-year holding period for tax-free earnings withdrawals",
                    "Income limits may require backdoor Roth strategy",
                    "Consider current vs. future tax brackets"
                  ]}
                />
              </div>
            </div>
          </CollapsibleSection>

          <CollapsibleSection 
            title="Health Savings Accounts (HSA)" 
            icon={<Heart className="w-6 h-6 text-emerald-500" />}
          >
            <div className="space-y-6">
              <p className="text-slate-300 leading-relaxed">
                Health Savings Accounts (HSAs) offer unique advantages that make them powerful retirement saving tools when used strategically.
              </p>

              <div className="grid md:grid-cols-2 gap-6">
                <div className="glass-card p-6 border border-emerald-500/20">
                  <h3 className="text-xl font-semibold text-emerald-500 mb-3">Tax Benefits</h3>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-2">
                      <ArrowRight className="w-5 h-5 text-emerald-500 mt-1 flex-shrink-0" />
                      <p className="text-slate-300">Pre-tax contributions reduce your taxable income</p>
                    </li>
                    <li className="flex items-start gap-2">
                      <ArrowRight className="w-5 h-5 text-emerald-500 mt-1 flex-shrink-0" />
                      <p className="text-slate-300">Tax-free growth through investments</p>
                    </li>
                    <li className="flex items-start gap-2">
                      <ArrowRight className="w-5 h-5 text-emerald-500 mt-1 flex-shrink-0" />
                      <p className="text-slate-300">Tax-free withdrawals for qualified medical expenses</p>
                    </li>
                  </ul>
                  <EligibilityGuide
                    requirements={[
                      "Must be enrolled in a High Deductible Health Plan (HDHP)",
                      "HDHP minimum deductible: $1,600 single/$3,200 family (2024)",
                      "Cannot be enrolled in Medicare",
                      "Cannot be claimed as a dependent on someone's tax return"
                    ]}
                    benefits={[
                      "Triple tax advantage: deductible contributions, tax-free growth, tax-free withdrawals",
                      "2024 contribution limits: $4,150 single/$8,300 family",
                      "$1,000 catch-up contributions at age 55+",
                      "Can invest funds for long-term growth"
                    ]}
                    considerations={[
                      "Higher out-of-pocket costs with HDHP",
                      "Must keep receipts for tax-free withdrawals",
                      "20% penalty on non-qualified withdrawals before age 65",
                      "Compare HDHP vs. traditional health plan total costs"
                    ]}
                  />
                </div>

                <div className="glass-card p-6 border border-emerald-500/20">
                  <h3 className="text-xl font-semibold text-emerald-500 mb-3">Strategic Usage</h3>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-2">
                      <ArrowRight className="w-5 h-5 text-emerald-500 mt-1 flex-shrink-0" />
                      <p className="text-slate-300">Pay medical expenses out-of-pocket, let HSA investments grow</p>
                    </li>
                    <li className="flex items-start gap-2">
                      <ArrowRight className="w-5 h-5 text-emerald-500 mt-1 flex-shrink-0" />
                      <p className="text-slate-300">Save receipts for tax-free withdrawals years later</p>
                    </li>
                    <li className="flex items-start gap-2">
                      <ArrowRight className="w-5 h-5 text-emerald-500 mt-1 flex-shrink-0" />
                      <p className="text-slate-300">After 65, use like a traditional IRA with no penalties</p>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="bg-emerald-500/5 p-4 rounded-lg border border-emerald-500/20">
                <h4 className="text-emerald-500 font-medium mb-2">Learn More About HSAs</h4>
                <div className="grid md:grid-cols-2 gap-4">
                  <a 
                    href="https://www.irs.gov/publications/p969#en_US_2022_publink1000204083"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 p-3 bg-emerald-500/10 rounded-lg hover:bg-emerald-500/20 transition-colors"
                  >
                    <ExternalLink className="w-4 h-4 text-emerald-500" />
                    <span className="text-sm text-slate-300">IRS HSA Guidelines</span>
                  </a>
                  <a 
                    href="https://www.fidelity.com/go/hsa/why-hsa"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 p-3 bg-emerald-500/10 rounded-lg hover:bg-emerald-500/20 transition-colors"
                  >
                    <ExternalLink className="w-4 h-4 text-emerald-500" />
                    <span className="text-sm text-slate-300">Fidelity HSA Overview</span>
                  </a>
                  <a 
                    href="https://healthequity.com/hsa/what-is-an-hsa"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 p-3 bg-emerald-500/10 rounded-lg hover:bg-emerald-500/20 transition-colors"
                  >
                    <ExternalLink className="w-4 h-4 text-emerald-500" />
                    <span className="text-sm text-slate-300">HealthEquity HSA Guide</span>
                  </a>
                  <a 
                    href="https://www.optum.com/financial-services/hsa.html"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 p-3 bg-emerald-500/10 rounded-lg hover:bg-emerald-500/20 transition-colors"
                  >
                    <ExternalLink className="w-4 h-4 text-emerald-500" />
                    <span className="text-sm text-slate-300">Optum HSA Services</span>
                  </a>
                </div>
              </div>
            </div>
          </CollapsibleSection>

          <CollapsibleSection 
            title="Bank Account Optimization" 
            icon={<Building2 className="w-6 h-6 text-emerald-500" />}
          >
            <div className="space-y-6">
              <div className="glass-card p-6 border border-emerald-500/20">
                <h3 className="text-xl font-semibold text-emerald-500 mb-3">High-Yield Savings Accounts</h3>
                <p className="text-slate-300 mb-4">
                  Online banks typically offer significantly higher APY than traditional banks. Here are some of the top options available:
                </p>
                <div className="grid md:grid-cols-2 gap-4 mb-4">
                  <a 
                    href="https://www.sofi.com/banking"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-between p-3 bg-emerald-500/10 rounded-lg hover:bg-emerald-500/20 transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      <ExternalLink className="w-4 h-4 text-emerald-500" />
                      <span className="text-sm text-slate-300">SoFi Checking & Savings</span>
                    </div>
                    <span className="text-sm font-medium text-emerald-500">4.60% APY</span>
                  </a>
                  <a 
                    href="https://www.marcus.com/us/en/savings"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-between p-3 bg-emerald-500/10 rounded-lg hover:bg-emerald-500/20 transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      <ExternalLink className="w-4 h-4 text-emerald-500" />
                      <span className="text-sm text-slate-300">Marcus by Goldman Sachs</span>
                    </div>
                    <span className="text-sm font-medium text-emerald-500">4.50% APY</span>
                  </a>
                  <a 
                    href="https://www.ally.com/bank/online-savings-account/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-between p-3 bg-emerald-500/10 rounded-lg hover:bg-emerald-500/20 transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      <ExternalLink className="w-4 h-4 text-emerald-500" />
                      <span className="text-sm text-slate-300">Ally Bank</span>
                    </div>
                    <span className="text-sm font-medium text-emerald-500">4.35% APY</span>
                  </a>
                  <a 
                    href="https://www.discover.com/online-banking/savings-account/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-between p-3 bg-emerald-500/10 rounded-lg hover:bg-emerald-500/20 transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      <ExternalLink className="w-4 h-4 text-emerald-500" />
                      <span className="text-sm text-slate-300">Discover Bank</span>
                    </div>
                    <span className="text-sm font-medium text-emerald-500">4.30% APY</span>
                  </a>
                </div>
                <ul className="space-y-3">
                  <li className="flex items-start gap-2">
                    <ArrowRight className="w-5 h-5 text-emerald-500 mt-1 flex-shrink-0" />
                    <p className="text-slate-300">Look for accounts with no minimum balance requirements</p>
                  </li>
                  <li className="flex items-start gap-2">
                    <ArrowRight className="w-5 h-5 text-emerald-500 mt-1 flex-shrink-0" />
                    <p className="text-slate-300">Consider CD laddering for higher interest rates</p>
                  </li>
                </ul>
                <EligibilityGuide
                  requirements={[
                    "Must be 18 years or older",
                    "Valid Social Security number or Tax ID",
                    "U.S. resident with valid address",
                    "Government-issued ID"
                  ]}
                  benefits={[
                    "Higher interest rates than traditional banks",
                    "Often no monthly maintenance fees",
                    "FDIC insurance up to $250,000",
                    "Easy online account management"
                  ]}
                  considerations={[
                    "No physical branch access",
                    "May have transfer limits",
                    "Interest rates can change frequently",
                    "Consider account features beyond APY"
                  ]}
                />
              </div>

              <div className="glass-card p-6 border border-emerald-500/20">
                <h3 className="text-xl font-semibold text-emerald-500 mb-3">Bank Account Bonuses</h3>
                <div className="space-y-4">
                  <p className="text-slate-300 leading-relaxed">
                    Bank account churning is a strategic approach to earning extra income by opening new bank accounts to receive sign-up bonuses. This process can be highly profitable when done systematically and organized properly.
                  </p>
                  <div className="bg-emerald-500/5 p-4 rounded-lg border border-emerald-500/20">
                    <h4 className="text-emerald-500 font-medium mb-2 flex items-center gap-2">
                      <ExternalLink className="w-4 h-4" />
                      Bank Bonus Tracking Spreadsheet
                    </h4>
                    <p className="text-slate-300 text-sm mb-3">
                      We've created a comprehensive spreadsheet to help you track and maximize bank account bonuses. This tool helps you:
                    </p>
                    <ul className="space-y-2 mb-4">
                      <li className="flex items-start gap-2 text-sm">
                        <ArrowRight className="w-4 h-4 text-emerald-500 mt-1 flex-shrink-0" />
                        <p className="text-slate-300">Track multiple bank bonus opportunities simultaneously</p>
                      </li>
                      <li className="flex items-start gap-2 text-sm">
                        <ArrowRight className="w-4 h-4 text-emerald-500 mt-1 flex-shrink-0" />
                        <p className="text-slate-300">Monitor important dates and requirements</p>
                      </li>
                      <li className="flex items-start gap-2 text-sm">
                        <ArrowRight className="w-4 h-4 text-emerald-500 mt-1 flex-shrink-0" />
                        <p className="text-slate-300">Calculate potential earnings and time investments</p>
                      </li>
                      <li className="flex items-start gap-2 text-sm">
                        <ArrowRight className="w-4 h-4 text-emerald-500 mt-1 flex-shrink-0" />
                        <p className="text-slate-300">Plan account closures to avoid fees</p>
                      </li>
                    </ul>
                    <a 
                      href="https://docs.google.com/spreadsheets/d/16F4akEk7_mzJ4jtKsU0SFjAe_8vsjKw6Wb5PwEp2ISk/edit?usp=sharing"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-500/10 text-emerald-500 rounded-lg hover:bg-emerald-500/20 transition-colors text-sm font-medium"
                    >
                      Access Tracking Spreadsheet
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  </div>
                  <ul className="space-y-3 mt-4">
                    <li className="flex items-start gap-2">
                      <ArrowRight className="w-5 h-5 text-emerald-500 mt-1 flex-shrink-0" />
                      <p className="text-slate-300">Many banks offer $200-$500 for opening new accounts</p>
                    </li>
                    <li className="flex items-start gap-2">
                      <ArrowRight className="w-5 h-5 text-emerald-500 mt-1 flex-shrink-0" />
                      <p className="text-slate-300">Track requirements and dates in a spreadsheet</p>
                    </li>
                    <li className="flex items-start gap-2">
                      <ArrowRight className="w-5 h-5 text-emerald-500 mt-1 flex-shrink-0" />
                      <p className="text-slate-300">Remember bonuses are taxable income</p>
                    </li>
                  </ul>
                  <EligibilityGuide
                    requirements={[
                      "Good ChexSystems record (tracks bank account history)",
                      "Ability to meet minimum deposit requirements",
                      "Sufficient funds to maintain minimum balances",
                      "Clean banking history with no forced account closures"
                    ]}
                    benefits={[
                      "Easy way to earn $1,000+ annually",
                      "Can combine with high-yield savings accounts",
                      "Some bonuses post quickly (within 30 days)",
                      "Can often repeat bonuses after waiting period"
                    ]}
                    considerations={[
                      "Keep track of account opening/closing dates",
                      "Watch for early account closure fees",
                      "Monitor direct deposit requirements",
                      "May impact ability to get future bank accounts"
                    ]}
                  />
                </div>
              </div>
            </div>
          </CollapsibleSection>

          <CollapsibleSection 
            title="Workplace Benefits" 
            icon={<Briefcase className="w-6 h-6 text-emerald-500" />}
          >
            <div className="grid md:grid-cols-2 gap-6">
              <div className="glass-card p-6 border border-emerald-500/20">
                <h3 className="text-xl font-semibold text-emerald-500 mb-3">FSA Maximization</h3>
                <ul className="space-y-3">
                  <li className="flex items-start gap-2">
                    <ArrowRight className="w-5 h-5 text-emerald-500 mt-1 flex-shrink-0" />
                    <p className="text-slate-300">Contribute up to $3,200 (2024) for healthcare FSA</p>
                  </li>
                  <li className="flex items-start gap-2">
                    <ArrowRight className="w-5 h-5 text-emerald-500 mt-1 flex-shrink-0" />
                    <p className="text-slate-300">Use FSA for eligible expenses before HSA funds</p>
                  </li>
                  <li className="flex items-start gap-2">
                    <ArrowRight className="w-5 h-5 text-emerald-500 mt-1 flex-shrink-0" />
                    <p className="text-slate-300">Plan major purchases around FSA deadlines</p>
                  </li>
                </ul>
                <EligibilityGuide
                  requirements={[
                    "Must be offered by your employer",
                    "Cannot be self-employed",
                    "Cannot have both FSA and HSA (usually)",
                    "Must elect during open enrollment"
                  
                  ]}
                  benefits={[
                    "Immediate access to full year's contribution",
                    "Pre-tax contributions reduce taxable income",
                    "Can use for family medical expenses",
                    "Some employers offer grace period or rollover"
                  ]}
                  considerations={[
                    "Use-it-or-lose-it rule applies",
                    "Limited to $3,200 annual contribution (2024)",
                    "Must estimate expenses carefully",
                    "Job change may forfeit remaining balance"
                  ]}
                />
              </div>

              <div className="glass-card p-6 border border-emerald-500/20">
                <h3 className="text-xl font-semibold text-emerald-500 mb-3">Employee Stock Purchase Plan (ESPP)</h3>
                <ul className="space-y-3">
                  <li className="flex items-start gap-2">
                    <ArrowRight className="w-5 h-5 text-emerald-500 mt-1 flex-shrink-0" />
                    <p className="text-slate-300">Purchase company stock at a discount (typically 15%)</p>
                  </li>
                  <li className="flex items-start gap-2">
                    <ArrowRight className="w-5 h-5 text-emerald-500 mt-1 flex-shrink-0" />
                    <p className="text-slate-300">Sell immediately after purchase for guaranteed return</p>
                  </li>
                  <li className="flex items-start gap-2">
                    <ArrowRight className="w-5 h-5 text-emerald-500 mt-1 flex-shrink-0" />
                    <p className="text-slate-300">Consider tax implications of holding vs. selling</p>
                  </li>
                </ul>
                <EligibilityGuide
                  requirements={[
                    "Must be offered by your employer",
                    "Usually need to be full-time employee",
                    "May have minimum employment period",
                    "Must enroll during offering period"
                  ]}
                  benefits={[
                    "Guaranteed return through discount",
                    "Potential for additional stock appreciation",
                    "Payroll deduction makes saving automatic",
                    "Can sell immediately to lock in discount"
                  ]}
                  considerations={[
                    "Consider company stock concentration risk",
                    "Understand qualifying vs. disqualifying disposition",
                    "Watch contribution limits (usually $25,000/year)",
                    "Plan sales around tax implications"
                  ]}
                />
              </div>
            </div>
          </CollapsibleSection>

          <CollapsibleSection 
            title="Automated Savings" 
            icon={<Target className="w-6 h-6 text-emerald-500" />}
          >
            <div className="grid md:grid-cols-2 gap-6">
              <div className="glass-card p-6 border border-emerald-500/20">
                <h3 className="text-xl font-semibold text-emerald-500 mb-3">Round-Up Apps</h3>
                <ul className="space-y-3">
                  <li className="flex items-start gap-2">
                    <ArrowRight className="w-5 h-5 text-emerald-500 mt-1 flex-shrink-0" />
                    <p className="text-slate-300">Acorns: Invest spare change from purchases</p>
                  </li>
                  <li className="flex items-start gap-2">
                    <ArrowRight className="w-5 h-5 text-emerald-500 mt-1 flex-shrink-0" />
                    <p className="text-slate-300">Qapital: Create custom rules for automated savings</p>
                  </li>
                  <li className="flex items-start gap-2">
                    <ArrowRight className="w-5 h-5 text-emerald-500 mt-1 flex-shrink-0" />
                    <p className="text-slate-300">Digit: AI-powered savings based on spending patterns</p>
                  </li>
                </ul>
                <EligibilityGuide
                  requirements={[
                    "Must be 18 years or older",
                    "Have a checking account",
                    "Valid debit or credit card",
                    "Smartphone for app access"
                  ]}
                  benefits={[
                    "Automated savings without thinking about it",
                    "Can start with very small amounts",
                    "Many apps include investment options",
                    "Helps build saving habits"
                  ]}
                  considerations={[
                    "Monthly fees may eat into small balances",
                    "Limited investment options",
                    "May need to link multiple accounts",
                    "Consider security of linked accounts"
                  ]}
                />
              </div>

              <div className="glass-card p-6 border border-emerald-500/20">
                <h3 className="text-xl font-semibold text-emerald-500 mb-3">Automatic Transfer Rules</h3>
                <ul className="space-y-3">
                  <li className="flex items-start gap-2">
                    <ArrowRight className="w-5 h-5 text-emerald-500 mt-1 flex-shrink-0" />
                    <p className="text-slate-300">Split direct deposits between accounts</p>
                  </li>
                  <li className="flex items-start gap-2">
                    <ArrowRight className="w-5 h-5 text-emerald-500 mt-1 flex-shrink-0" />
                    <p className="text-slate-300">Schedule transfers on payday to savings</p>
                  </li>
                  <li className="flex items-start gap-2">
                    <ArrowRight className="w-5 h-5 text-emerald-500 mt-1 flex-shrink-0" />
                    <p className="text-slate-300">Create separate accounts for different goals</p>
                  </li>
                </ul>
                <EligibilityGuide
                  requirements={[
                    "Regular income source",
                    "Bank accounts that support automatic transfers",
                    "Online banking access",
                    "Sufficient buffer to avoid overdrafts"
                  ]}
                  benefits={[
                    "No apps or third-party services needed",
                    "Complete control over transfer timing",
                    "No fees beyond standard bank fees",
                    "Can adjust amounts easily"
                  ]}
                  considerations={[
                    "Need to maintain minimum balances",
                    "Watch for transfer limits",
                    "Keep track of multiple accounts",
                    "Plan for irregular income"
                  ]}
                />
              </div>
            </div>
          </CollapsibleSection>

          <CollapsibleSection 
            title="Smart Shopping" 
            icon={<ShoppingBag className="w-6 h-6 text-emerald-500" />}
          >
            <div className="grid md:grid-cols-2 gap-6">
              <div className="glass-card p-6 border border-emerald-500/20">
                <h3 className="text-xl font-semibold text-emerald-500 mb-3">Cashback Optimization</h3>
                <ul className="space-y-3">
                  <li className="flex items-start gap-2">
                    <ArrowRight className="w-5 h-5 text-emerald-500 mt-1 flex-shrink-0" />
                    <p className="text-slate-300">Rakuten: Earn up to 40% cashback at select retailers</p>
                  </li>
                  <li className="flex items-start gap-2">
                    <ArrowRight className="w-5 h-5 text-emerald-500 mt-1 flex-shrink-0" />
                    <p className="text-slate-300">TopCashback: Often offers highest cashback rates</p>
                  </li>
                  <li className="flex items-start gap-2">
                    <ArrowRight className="w-5 h-5 text-emerald-500 mt-1 flex-shrink-0" />
                    <p className="text-slate-300">Stack with credit card rewards for maximum returns</p>
                  </li>
                  <li className="flex items-start gap-2">
                    <Fuel className="w-5 h-5 text-emerald-500 mt-1 flex-shrink-0" />
                    <div className="space-y-1">
                      <p className="text-slate-300">Upside: Save on gas purchases at participating stations</p>
                      <a 
                        href="https://upside.app.link/DREW22832"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-emerald-500 hover:text-emerald-400 flex items-center gap-1"
                      >
                        Get the Upside app
                        <ExternalLink className="w-4 h-4" />
                      </a>
                    </div>
                  </li>
                </ul>
                <EligibilityGuide
                  requirements={[
                    "Valid email address",
                    "U.S. resident for most programs",
                    "Credit or debit card for purchases",
                    "Smartphone for app-based programs"
                  ]}
                  benefits={[
                    "Stack multiple rewards programs",
                    "No cost to join most programs",
                    "Can earn hundreds annually",
                    "Works with existing shopping habits"
                  ]}
                  considerations={[
                    "Don't buy just for cashback",
                    "Compare prices across retailers",
                    "Watch for tracking issues",
                    "Payment method restrictions"
                  ]}
                />
              </div>

              <div className="glass-card p-6 border border-emerald-500/20">
                <h3 className="text-xl font-semibold text-emerald-500 mb-3">Price Tracking Tools</h3>
                <ul className="space-y-3">
                  <li className="flex items-start gap-2">
                    <ArrowRight className="w-5 h-5 text-emerald-500 mt-1 flex-shrink-0" />
                    <p className="text-slate-300">CamelCamelCamel: Amazon price history tracking</p>
                  </li>
                  <li className="flex items-start gap-2">
                    <ArrowRight className="w-5 h-5 text-emerald-500 mt-1 flex-shrink-0" />
                    <p className="text-slate-300">Honey: Automatic coupon code testing</p>
                  </li>
                  <li className="flex items-start gap-2">
                    <ArrowRight className="w-5 h-5 text-emerald-500 mt-1 flex-shrink-0" />
                    <p className="text-slate-300">Capital One Shopping: Price comparison across retailers</p>
                  </li>
                </ul>
                <EligibilityGuide
                  requirements={[
                    "Web browser for extensions",
                    "Email for price alerts",
                    "Online shopping accounts",
                    "Patience to wait for price drops"
                  ]}
                  benefits={[
                    "Save 10-50% on purchases",
                    "Automated price monitoring",
                    "Historical price data",
                    "Price protection features"
                  ]}
                  considerations={[
                    "Privacy implications of tracking",
                    "May delay needed purchases",
                    "Not all retailers supported",
                    "Browser extension security"
                  ]}
                />
              </div>
            </div>
          </CollapsibleSection>

          <CollapsibleSection 
            title="Alternative Savings" 
            icon={<Coins className="w-6 h-6 text-emerald-500" />}
          >
            <div className="grid md:grid-cols-2 gap-6">
              <div className="glass-card p-6 border border-emerald-500/20">
                <h3 className="text-xl font-semibold text-emerald-500 mb-3">529 College Savings</h3>
                <ul className="space-y-3">
                  <li className="flex items-start gap-2">
                    <GraduationCap className="w-5 h-5 text-emerald-500 mt-1 flex-shrink-0" />
                    <p className="text-slate-300">Tax-advantaged education savings with state tax benefits</p>
                  </li>
                  <li className="flex items-start gap-2">
                    <Gift className="w-5 h-5 text-emerald-500 mt-1 flex-shrink-0" />
                    <p className="text-slate-300">Can be used for K-12 tuition up to $10,000 annually</p>
                  </li>
                  <li className="flex items-start gap-2">
                    <DollarSign className="w-5 h-5 text-emerald-500 mt-1 flex-shrink-0" />
                    <p className="text-slate-300">Beneficiary can be changed to family members</p>
                  </li>
                </ul>
                <EligibilityGuide
                  requirements={[
                    "Valid Social Security number",
                    "U.S. citizen or resident alien",
                    "No income limits",
                    "Can open for anyone (child, grandchild, self)"
                  ]}
                  benefits={[
                    "State tax deduction in many states",
                    "Tax-free growth for education",
                    "High contribution limits",
                    "Gift tax advantages"
                  ]}
                  considerations={[
                    "Penalties for non-qualified withdrawals",
                    "Limited investment options",
                    "Impact on financial aid",
                    "State tax implications"
                  ]}
                />
              </div>

              <div className="glass-card p-6 border border-emerald-500/20">
                <h3 className="text-xl font-semibold text-emerald-500 mb-3">Credit Card Rewards</h3>
                <ul className="space-y-3">
                  <li className="flex items-start gap-2">
                    <CreditCard className="w-5 h-5 text-emerald-500 mt-1 flex-shrink-0" />
                    <div className="space-y-1">
                      <p className="text-slate-300">American Express Gold Card: Earn rewards on dining and groceries</p>
                      <a 
                        href="https://americanexpress.com/en-us/referral/gold-card?ref=ANDREYVhmR&XL=MIMNS"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-emerald-500 hover:text-emerald-400 flex items-center gap-1"
                      >
                        Apply with referral bonus
                        <ExternalLink className="w-4 h-4" />
                      </a>
                    </div>
                  </li>
                  <li className="flex items-start gap-2">
                    <Wallet className="w-5 h-5 text-emerald-500 mt-1 flex-shrink-0" />
                    <p className="text-slate-300">Stack rewards with shopping portals and promotions</p>
                  </li>
                  <li className="flex items-start gap-2">
                    <Building2 className="w-5 h-5 text-emerald-500 mt-1 flex-shrink-0" />
                    <p className="text-slate-300">Business cards often have higher bonuses and benefits</p>
                  </li>
                </ul>
                <EligibilityGuide
                  requirements={[
                    "Good to excellent credit score (usually 670+)",
                    "Stable income source",
                    "U.S. resident with SSN",
                    "Clean credit history"
                  ]}
                  benefits={[
                    "Sign-up bonuses worth $500-$1,000+",
                    "Ongoing rewards on spending",
                    "Travel perks and protections",
                    "Purchase protection benefits"
                  ]}
                  considerations={[
                    "Annual fees on premium cards",
                    "Need to pay in full monthly",
                    "Requires good credit habits",
                    "Watch for reward expiration"
                  ]}
                />
              </div>
            </div>
          </CollapsibleSection>

          <CollapsibleSection 
            title="Advanced Rewards Optimization" 
            icon={<CreditCard className="w-6 h-6 text-emerald-500" />}
          >
            <div className="space-y-6">
              <div className="glass-card p-6 border border-emerald-500/20">
                <h3 className="text-xl font-semibold text-emerald-500 mb-3">Credit Card Points Maximization</h3>
                <div className="space-y-4">
                  <div className="glass-card p-4 border border-emerald-500/10">
                    <h4 className="font-medium text-emerald-500 mb-2">Optimal Card Combinations</h4>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <p className="text-sm font-medium text-slate-300">Everyday Spending</p>
                        <ul className="space-y-1 text-sm text-slate-400">
                          <li>• Groceries: Amex Gold (4x points)</li>
                          <li>• Dining: Chase Sapphire Reserve (3x points)</li>
                          <li>• Travel: Capital One Venture X (10x on hotels)</li>
                          <li>• Gas: Citi Premier (3x points)</li>
                        </ul>
                      </div>
                      <div className="space-y-2">
                        <p className="text-sm font-medium text-slate-300">Bonus Categories</p>
                        <ul className="space-y-1 text-sm text-slate-400">
                          <li>• Online Shopping: Chase Freedom Flex (5% rotating)</li>
                          <li>• Entertainment: Capital One Savor (4% back)</li>
                          <li>• Business: Amex Business Gold (4x select categories)</li>
                          <li>• Utilities: Ink Business Cash (5% on internet/phone)</li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  <div className="glass-card p-4 border border-emerald-500/10">
                    <h4 className="font-medium text-emerald-500 mb-2">Sign-up Bonus Strategy</h4>
                    <div className="space-y-3">
                      <p className="text-sm text-slate-300">Timing Considerations:</p>
                      <ul className="space-y-1 text-sm text-slate-400">
                        <li>• Apply during highest bonus offers (typically Q4)</li>
                        <li>• Space applications 3-4 months apart</li>
                        <li>• Track application rules (5/24, once per lifetime, etc.)</li>
                        <li>• Consider business card options to preserve personal slots</li>
                      </ul>
                      <div className="mt-4">
                        <p className="text-sm text-slate-300">Meeting Minimum Spend:</p>
                        <ul className="space-y-1 text-sm text-slate-400">
                          <li>• Time large planned purchases with new cards</li>
                          <li>• Prepay utilities and insurance</li>
                          <li>• Add authorized users for combined spending</li>
                          <li>• Pay rent/mortgage (factor in processing fees)</li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  <div className="glass-card p-4 border border-emerald-500/10">
                    <h4 className="font-medium text-emerald-500 mb-2">Point Transfer Strategy</h4>
                    <div className="space-y-3">
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <p className="text-sm font-medium text-slate-300">Best Transfer Partners</p>
                          <ul className="space-y-1 text-sm text-slate-400">
                            <li>• Chase → United (1:1)</li>
                            <li>• Amex → ANA (1:1)</li>
                            <li>• Capital One → Turkish (1:1)</li>
                            <li>• Citi → Virgin Atlantic (1:1)</li>
                          </ul>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-slate-300">Transfer Bonuses</p>
                          <ul className="space-y-1 text-sm text-slate-400">
                            <li>• Wait for transfer promotions (up to 40%)</li>
                            <li>• Stack with award sales</li>
                            <li>• Consider hotel transfers during bonuses</li>
                            <li>• Monitor limited-time partnerships</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="glass-card p-6 border border-emerald-500/20">
                <h3 className="text-xl font-semibold text-emerald-500 mb-3">Subscription Cost Reduction</h3>
                <div className="space-y-4">
                  <div className="glass-card p-4 border border-emerald-500/10">
                    <h4 className="font-medium text-emerald-500 mb-2">Family Plan Optimization</h4>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm font-medium text-slate-300">Streaming Services</p>
                        <table className="w-full text-sm">
                          <tbody>
                            <tr className="text-slate-400">
                              <td>Netflix Premium</td>
                              <td className="text-right">$6.75/person (4 users)</td>
                            </tr>
                            <tr className="text-slate-400">
                              <td>Spotify Family</td>
                              <td className="text-right">$2.50/person (6 users)</td>
                            </tr>
                            <tr className="text-slate-400">
                              <td>YouTube Premium</td>
                              <td className="text-right">$3/person (5 users)</td>
                            </tr>
                            <tr className="text-slate-400">
                              <td>Apple One Family</td>
                              <td className="text-right">$5/person (6 users)</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-slate-300">Other Services</p>
                        <table className="w-full text-sm">
                          <tbody>
                            <tr className="text-slate-400">
                              <td>Amazon Prime</td>
                              <td className="text-right">Share with household</td>
                            </tr>
                            <tr className="text-slate-400">
                              <td>Microsoft 365</td>
                              <td className="text-right">$2/person (6 users)</td>
                            </tr>
                            <tr className="text-slate-400">
                              <td>Nintendo Online</td>
                              <td className="text-right">$7/person (8 users)</td>
                            </tr>
                            <tr className="text-slate-400">
                              <td>Costco Membership</td>
                              <td className="text-right">Share card (2 users)</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>

                  <div className="glass-card p-4 border border-emerald-500/10">
                    <h4 className="font-medium text-emerald-500 mb-2">Negotiation Strategies</h4>
                    <div className="space-y-3">
                      <p className="text-sm text-slate-300">Service Provider Tips:</p>
                      <ul className="space-y-1 text-sm text-slate-400">
                        <li>• Research competitor offers before calling</li>
                        <li>• Call during business hours (better agents)</li>
                        <li>• Ask for retention or cancellation department</li>
                        <li>• Reference specific competitor offers</li>
                        <li>• Be polite but firm about leaving</li>
                      </ul>
                      <div className="mt-4">
                        <p className="text-sm text-slate-300">Annual Savings Potential:</p>
                        <ul className="space-y-1 text-sm text-slate-400">
                          <li>• Internet: $20-40/month ($240-480/year)</li>
                          <li>• Phone: $15-30/month ($180-360/year)</li>
                          <li>• Insurance: $100-300/year</li>
                          <li>• Streaming: $60-120/year</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="glass-card p-6 border border-emerald-500/20">
                <h3 className="text-xl font-semibold text-emerald-500 mb-3">Advanced Savings Techniques</h3>
                <div className="space-y-4">
                  <div className="glass-card p-4 border border-emerald-500/10">
                    <h4 className="font-medium text-emerald-500 mb-2">Cashback Stacking</h4>
                    <div className="space-y-3">
                      <p className="text-sm text-slate-300">Example Stack:</p>
                      <div className="space-y-1 text-sm text-slate-400">
                        <p>1. Credit Card (2-5% back)</p>
                        <p>2. Shopping Portal (1-10% back)</p>
                        <p>3. Store Rewards (1-5% back)</p>
                        <p>4. Coupon Codes (5-20% off)</p>
                        <p>5. Discounted Gift Cards (2-20% off)</p>
                        <p><strong>Total Savings: 11-60% off retail</strong></p>
                      </div>
                    </div>
                  </div>

                  <div className="glass-card p-4 border border-emerald-500/10">
                    <h4 className="font-medium text-emerald-500 mb-2">Gift Card Arbitrage</h4>
                    <div className="space-y-3">
                      <p className="text-sm text-slate-300">Best Practices:</p>
                      <ul className="space-y-1 text-sm text-slate-400">
                        <li>• Buy discounted gift cards (5-20% off)</li>
                        <li>• Stack with store sales and coupons</li>
                        <li>• Resell unwanted gift cards (80-92% of value)</li>
                        <li>• Monitor gift card promotions (bonus value)</li>
                      </ul>
                      <div className="mt-4 p-3 bg-emerald-500/5 rounded-lg">
                        <p className="text-sm text-emerald-500">Safety Tips:</p>
                        <ul className="space-y-1 text-sm text-slate-400">
                          <li>• Only use reputable gift card marketplaces</li>
                          <li>• Check balance immediately after purchase</li>
                          <li>• Keep all transaction records</li>
                          <li>• Use cards quickly after purchase</li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  <div className="glass-card p-4 border border-emerald-500/10">
                    <h4 className="font-medium text-emerald-500 mb-2">Loyalty Program Optimization</h4>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm font-medium text-slate-300">Program Types</p>
                        <ul className="space-y-1 text-sm text-slate-400">
                          <li>• Retail Store Programs</li>
                          <li>• Grocery Store Programs</li>
                          <li>• Restaurant Rewards</li>
                          <li>• Gas Station Programs</li>
                          <li>• Hotel Loyalty Programs</li>
                        </ul>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-slate-300">Maximization Tips</p>
                        <ul className="space-y-1 text-sm text-slate-400">
                          <li>• Link to payment cards</li>
                          <li>• Stack with other rewards</li>
                          <li>• Watch for bonus periods</li>
                          <li>• Use points strategically</li>
                          <li>• Track expiration dates</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </CollapsibleSection>
        </div>
      </div>
    </div>
  );
};

export default SavingsBlog;